import { z } from 'zod/v3';
import { WorkflowOutputSchema, WorkflowParamSchema, StepValidationPolicySchema } from './workflow.dto';

/**
 * Workflow transfer payload (import/export).
 * Uses a tolerant step schema for backward/forward compatibility.
 */
export const WorkflowTransferStepSchema = z
  .object({
    id: z.string().min(1),
    action: z.string().min(1),
    intent: z.string().optional(),
    prompt: z.string().optional(),
    hints: z.record(z.unknown()).optional(),
    validation: StepValidationPolicySchema.optional(),
  })
  .passthrough();

export type WorkflowTransferStep = z.infer<typeof WorkflowTransferStepSchema>;

export const WorkflowTransferDefinitionSchema = z
  .object({
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    params: z.record(WorkflowParamSchema).default({}),
    steps: z.array(WorkflowTransferStepSchema).default([]),
    documentation: z.unknown().nullable().optional(),
    outputs: z.array(WorkflowOutputSchema).nullable().optional(),
    workflowAssertions: z.unknown().nullable().optional(),
    successCriteria: z.string().nullable().optional(),
    metadata: z.record(z.unknown()).default({}),
    compilerVersion: z.string().default('1.0.0'),
    schemaVersion: z.number().int().positive().default(1),
    sourceDocVersion: z.number().int().nullable().optional(),
    sourceDocHash: z.string().nullable().optional(),
  })
  .passthrough();

export type WorkflowTransferDefinition = z.infer<typeof WorkflowTransferDefinitionSchema>;

export const WorkflowTransferSourceSchema = z
  .object({
    workflowId: z.string().optional(),
    docId: z.string().optional(),
    workspaceId: z.string().optional(),
  })
  .optional();

export type WorkflowTransferSource = z.infer<typeof WorkflowTransferSourceSchema>;

export const WorkflowTransferFileSchema = z
  .object({
    format: z.literal('deepguide.workflow'),
    version: z.literal(1),
    exportedAt: z.string(),
    source: WorkflowTransferSourceSchema,
    workflow: WorkflowTransferDefinitionSchema,
    integrity: z
      .object({
        definitionHash: z.string().min(1),
      })
      .optional(),
  })
  .passthrough();

export type WorkflowTransferFile = z.infer<typeof WorkflowTransferFileSchema>;

export const WorkflowExportRequestSchema = z.object({
  workflowId: z.string(),
  includeDocReference: z.boolean().optional().default(true),
});

export type WorkflowExportRequest = z.infer<typeof WorkflowExportRequestSchema>;

export const WorkflowExportResultSchema = z.object({
  fileName: z.string(),
  definitionHash: z.string(),
  payload: WorkflowTransferFileSchema,
  json: z.string(),
});

export type WorkflowExportResult = z.infer<typeof WorkflowExportResultSchema>;

export const WorkflowImportConflictPolicySchema = z.enum(['error', 'rename', 'replace']);

export type WorkflowImportConflictPolicy = z.infer<typeof WorkflowImportConflictPolicySchema>;

export const WorkflowImportPreviewRequestSchema = z.object({
  json: z.string().min(1),
  targetDocId: z.string().optional(),
});

export type WorkflowImportPreviewRequest = z.infer<typeof WorkflowImportPreviewRequestSchema>;

export const WorkflowImportPreviewResultSchema = z.object({
  name: z.string(),
  suggestedName: z.string(),
  stepCount: z.number().int().nonnegative(),
  paramCount: z.number().int().nonnegative(),
  definitionHash: z.string(),
  hasNameConflict: z.boolean(),
  existingByNameId: z.string().optional(),
  existingByHashId: z.string().optional(),
  requiresTargetDoc: z.boolean(),
  resolvedDocId: z.string().nullable(),
  source: WorkflowTransferSourceSchema,
});

export type WorkflowImportPreviewResult = z.infer<typeof WorkflowImportPreviewResultSchema>;

export const WorkflowImportRequestSchema = z.object({
  json: z.string().min(1),
  targetDocId: z.string().optional(),
  conflictPolicy: WorkflowImportConflictPolicySchema.optional().default('rename'),
});

export type WorkflowImportRequest = z.infer<typeof WorkflowImportRequestSchema>;

export const WorkflowImportResultSchema = z.object({
  workflowId: z.string(),
  mode: z.enum(['created', 'updated']),
  name: z.string(),
  definitionHash: z.string(),
  targetDocId: z.string(),
  conflictPolicy: WorkflowImportConflictPolicySchema,
});

export type WorkflowImportResult = z.infer<typeof WorkflowImportResultSchema>;
